import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const PrivacyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="privacy-page">
      {/* Header */}
      <header className="py-6 px-6 md:px-12 border-b border-[#262626]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-['Oswald'] text-xl md:text-2xl font-bold tracking-tight" data-testid="privacy-logo-link">
            WILTSHIRE<span className="text-[#FF4500]">WEB</span><span className="text-[#888888] font-normal ml-1">STUDIO</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-[#888888] hover:text-white" data-testid="privacy-back-button">
              <ArrowLeft size={18} className="mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="py-16 px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <h1 className="font-['Oswald'] text-4xl md:text-5xl text-white uppercase mb-4" data-testid="privacy-heading">
            Privacy Policy
          </h1>
          <p className="font-['Manrope'] text-[#888888] mb-12">
            Last updated: February 2026
          </p>

          <div className="prose prose-invert max-w-none space-y-8">
            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">1. Who I Am</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed">
                This website (wiltshirewebstudio.com) is operated by George, trading as Wiltshire Web Studio,
                a sole trader based in Wiltshire, United Kingdom. For the purposes of the UK GDPR and the
                Data Protection Act 2018, I am the data controller for any personal information you share with me
                via this website.
              </p>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                You can contact me at <a href="mailto:george@wiltshirewebstudio.com" className="text-[#FF4500] hover:underline">george@wiltshirewebstudio.com</a> or on <a href="tel:07767071806" className="text-[#FF4500] hover:underline">07767 071 806</a> about anything in this policy.
              </p>
            </section>

            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">2. What Information I Collect</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mb-4">
                I only collect information that you choose to give me, or that is necessary to run this website:
              </p>
              <ul className="font-['Manrope'] text-[#CCCCCC] leading-relaxed space-y-2 list-disc pl-6">
                <li><strong className="text-white">Contact details</strong> &mdash; if you email or call me, I'll have your name, email address, phone number and the content of your message.</li>
                <li><strong className="text-white">Project information</strong> &mdash; if you become a client, I'll hold the information you provide for your project (business details, content, imagery, login credentials for platforms etc.).</li>
                <li><strong className="text-white">Technical data</strong> &mdash; like most websites, my hosting provider automatically logs basic technical information such as your IP address, browser type and pages visited, for security and performance.</li>
              </ul>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                I do not use third-party advertising, tracking pixels or behavioural advertising cookies on this site.
              </p>
            </section>

            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">3. How I Use Your Information</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mb-4">
                I use your information to:
              </p>
              <ul className="font-['Manrope'] text-[#CCCCCC] leading-relaxed space-y-2 list-disc pl-6">
                <li>Respond to your enquiries and provide a quote.</li>
                <li>Deliver the services you've asked me to provide.</li>
                <li>Invoice you and keep accounting records.</li>
                <li>Keep the website secure and working properly.</li>
                <li>Comply with my legal obligations (for example, HMRC record-keeping).</li>
              </ul>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                My legal bases for processing are: performance of a contract (delivering services you've asked for),
                legitimate interests (running my small business, responding to enquiries, securing the site) and
                legal obligation (tax and accounting).
              </p>
            </section>

            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">4. Cookies</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed">
                This site uses only a minimal number of cookies and similar storage needed for the site to function
                (for example, remembering that you've dismissed the cookie notice). These are strictly necessary cookies
                and do not require your consent under UK law.
              </p>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                If I add optional analytics or marketing cookies in future, you will be asked to opt in first.
              </p>
            </section>

            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">5. Sharing Your Information</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mb-4">
                I don't sell your data. I only share it with trusted service providers I need to run my business, such as:
              </p>
              <ul className="font-['Manrope'] text-[#CCCCCC] leading-relaxed space-y-2 list-disc pl-6">
                <li>My hosting and email providers.</li>
                <li>My accountant and HMRC, where legally required.</li>
                <li>Platforms I use on your behalf (for example, your domain registrar or a CMS), only with your instruction.</li>
              </ul>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                Where any provider is based outside the UK/EEA, I rely on approved safeguards such as the UK International Data Transfer Agreement or adequacy regulations.
              </p>
            </section>

            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">6. How Long I Keep It</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed">
                Enquiry emails are kept for up to 2 years unless you become a client. Client project files and
                correspondence are kept for the duration of our working relationship and for at least 6 years afterwards
                to meet UK tax and accounting requirements. You can ask me to delete anything that isn't legally required.
              </p>
            </section>

            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">7. Your Rights</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mb-4">
                Under UK GDPR you have the right to:
              </p>
              <ul className="font-['Manrope'] text-[#CCCCCC] leading-relaxed space-y-2 list-disc pl-6">
                <li>Access a copy of the personal data I hold about you.</li>
                <li>Ask me to correct inaccurate data.</li>
                <li>Ask me to delete your data (where I'm not required to keep it).</li>
                <li>Object to, or restrict, how I use your data.</li>
                <li>Withdraw consent at any time where I'm relying on it.</li>
                <li>Lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#FF4500] hover:underline">ico.org.uk</a>.</li>
              </ul>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed mt-4">
                To exercise any of these, just email me at <a href="mailto:george@wiltshirewebstudio.com" className="text-[#FF4500] hover:underline">george@wiltshirewebstudio.com</a>.
              </p>
            </section>

            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">8. Security</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed">
                I take reasonable technical and organisational measures to keep your information safe, including HTTPS,
                strong passwords, and limiting access to your data to those who need it. No system is 100% secure,
                but I'll notify you promptly if anything changes that affects your personal data.
              </p>
            </section>

            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">9. Changes to This Policy</h2>
              <p className="font-['Manrope'] text-[#CCCCCC] leading-relaxed">
                If I update this policy, I'll update the "last updated" date at the top. Material changes will be
                highlighted on the home page for a reasonable time.
              </p>
            </section>

            <section>
              <h2 className="font-['Oswald'] text-2xl text-white uppercase mb-4">10. Contact</h2>
              <div className="font-['Manrope'] text-[#CCCCCC] leading-relaxed">
                <p className="text-white font-semibold">George &mdash; Wiltshire Web Studio</p>
                <p>Email: <a href="mailto:george@wiltshirewebstudio.com" className="text-[#FF4500] hover:underline">george@wiltshirewebstudio.com</a></p>
                <p>Phone: <a href="tel:07767071806" className="text-[#FF4500] hover:underline">07767 071 806</a></p>
                <p>Web: <a href="https://www.wiltshirewebstudio.com" className="text-[#FF4500] hover:underline">www.wiltshirewebstudio.com</a></p>
              </div>
            </section>
          </div>

          <div className="mt-16 pt-8 border-t border-[#262626]">
            <Link to="/">
              <Button className="btn-primary rounded-none font-['Oswald'] uppercase tracking-wider" data-testid="privacy-bottom-home-button">
                <ArrowLeft size={18} className="mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default PrivacyPage;
